import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Github, Instagram, Linkedin, Twitter } from 'lucide-react';
import { PANEL_BASE } from '../config/paths';

/** Social link config: key, href, icon, aria label key */
const SOCIAL_LINKS = [
  { key: 'twitter', href: 'https://twitter.com/buildmate', icon: Twitter, labelKey: 'footer.socialTwitter' },
  { key: 'linkedin', href: 'https://www.linkedin.com/company/buildmate-studio', icon: Linkedin, labelKey: 'footer.socialLinkedIn' },
  { key: 'instagram', href: 'https://www.instagram.com/buildmate.studio/', icon: Instagram, labelKey: 'footer.socialInstagram' },
  { key: 'github', href: 'https://github.com/buildmate-studio', icon: Github, labelKey: 'footer.socialGitHub' },
] as const;

/** Main footer menu (public site sections / panel) */
const MENU_LINKS = [
  { key: 'home', to: '/', labelKey: 'footer.menuHome' },
  { key: 'features', to: '/#features', labelKey: 'footer.menuFeatures' },
  { key: 'pricing', to: '/#pricing', labelKey: 'footer.menuPricing' },
  { key: 'faq', to: '/#faq', labelKey: 'footer.menuFaq' },
  { key: 'contact', to: '/#cta', labelKey: 'footer.menuContact' },
  { key: 'panel', to: PANEL_BASE, labelKey: 'footer.menuPanel' },
] as const;

/** Resources section links */
const RESOURCE_LINKS = [
  { to: '/about', labelKey: 'footer.resourcesAboutUs', external: false },
  { to: 'mailto:support@buildmate.com', labelKey: 'footer.resourcesEmailUs', external: true },
  { to: '/blog', labelKey: 'footer.resourcesBlog', external: false },
  { to: '/#faq', labelKey: 'footer.resourcesFaqTutorials', external: false },
  { to: '/videos', labelKey: 'footer.resourcesHowToVideos', external: false },
  { to: '/affiliates', labelKey: 'footer.resourcesAffiliates', external: false },
] as const;

/** Legal (sözleşme) links */
const LEGAL_LINKS = [
  { key: 'privacy', to: '/privacy', labelKey: 'footer.legalPrivacy' },
  { key: 'terms', to: '/terms', labelKey: 'footer.legalTerms' },
  { key: 'refund', to: '/refund', labelKey: 'footer.legalRefund' },
  { key: 'cookies', to: '/cookies', labelKey: 'footer.legalCookies' },
] as const;

export default function Footer() {
  const { t } = useTranslation();

  return (
    <footer className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 mt-auto">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Menü */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider mb-4">
              {t('footer.menuTitle')}
            </h3>
            <ul className="space-y-2">
              {MENU_LINKS.map(({ to, labelKey }) => (
                <li key={labelKey}>
                  {to.startsWith('http') ? (
                    <a
                      href={to}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                    >
                      {t(labelKey)}
                    </a>
                  ) : (
                    <Link
                      to={to}
                      onClick={to === '/' ? () => window.scrollTo({ top: 0, behavior: 'smooth' }) : undefined}
                      className="text-sm text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                    >
                      {t(labelKey)}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider mb-4">
              {t('footer.resourcesTitle')}
            </h3>
            <ul className="space-y-2">
              {RESOURCE_LINKS.map(({ to, labelKey, external }) => (
                <li key={labelKey}>
                  {external || to.startsWith('mailto:') || to.startsWith('http') ? (
                    <a
                      href={to}
                      target={to.startsWith('mailto:') ? undefined : '_blank'}
                      rel={to.startsWith('mailto:') ? undefined : 'noopener noreferrer'}
                      className="text-sm text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                    >
                      {t(labelKey)}
                    </a>
                  ) : (
                    <Link
                      to={to}
                      className="text-sm text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                    >
                      {t(labelKey)}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Legal (Sözleşmeler) */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider mb-4">
              {t('footer.legalTitle')}
            </h3>
            <ul className="space-y-2">
              {LEGAL_LINKS.map(({ to, labelKey }) => (
                <li key={labelKey}>
                  <Link
                    to={to}
                    className="text-sm text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                  >
                    {t(labelKey)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Sosyal medya */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider mb-4">
              {t('footer.socialTitle')}
            </h3>
            <div className="flex gap-4">
              {SOCIAL_LINKS.map(({ href, icon: Icon, labelKey }) => (
                <a
                  key={labelKey}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={t(labelKey)}
                  className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <Icon className="h-5 w-5" />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700 text-center text-sm text-gray-500 dark:text-gray-400">
          © {new Date().getFullYear()} {t('common.appName')}. {t('common.allRightsReserved')}
        </div>
      </div>
    </footer>
  );
}
